import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  IconPin, 
  IconCheck, 
  IconClose 
} from './Icons';
import './IntelligenceModal.css';

/**
 * Reusable Enterprise Modal Primitive for:
 * 1. Save Preset ('preset')
 * 2. Export Dataset ('export')
 * 3. Share View ('share')
 */
export default function IntelligenceModal({
  isOpen,
  type = 'preset', // 'preset' | 'export' | 'share'
  onClose,
  // district = 'all',
  district = 'tamilnadu',
  selectedSector = null,
  userPincode = null,
  onSavePreset,
  onExport,
}) {
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  // Preset State
  // const defaultPresetName = `${district === 'all' ? 'National' : district.toUpperCase()} · ${selectedSector ? `Sector ${selectedSector}` : 'All Sectors'}`;
  const defaultPresetName = `${(district === 'tamilnadu' || district === 'all') ? 'Tamil Nadu' : district.toUpperCase()} · ${selectedSector ? `Sector ${selectedSector}` : 'All Sectors'}`;
  const [presetTitle, setPresetTitle] = useState(defaultPresetName);
  const [presetError, setPresetError] = useState('');
  const [presetSaved, setPresetSaved] = useState(false);

  // Export State
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv' | 'excel' | 'pdf'
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Share State
  const [copied, setCopied] = useState(false);

  // Suggested preset names
  const suggestions = [
    // `${district === 'all' ? 'National' : district.toUpperCase()} · Growth Focus`,
    // `${district === 'all' ? 'Pan-India' : district.toUpperCase()} · Q3 Strategic Baseline`,
    `${(district === 'tamilnadu' || district === 'all') ? 'Tamil Nadu' : district.toUpperCase()} · Growth Focus`,
    `${(district === 'tamilnadu' || district === 'all') ? 'Tamil Nadu' : district.toUpperCase()} · Q3 Strategic Baseline`,
    `Sector Watch · ${selectedSector || 'Manufacturing'}`,
    `Regional Corridor · PIN ${userPincode || '641402'}`
  ];

  // Store previously focused element and auto-focus
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      setPresetTitle(defaultPresetName);
      setPresetError('');
      setPresetSaved(false);
      setIsExporting(false);
      setExportSuccess(false);
      setCopied(false);

      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      if (previouslyFocusedElementRef.current && typeof previouslyFocusedElementRef.current.focus === 'function') {
        previouslyFocusedElementRef.current.focus();
      }
    }
  }, [isOpen, defaultPresetName]);

  const handleSavePresetSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    const trimmed = presetTitle.trim();
    if (!trimmed) {
      setPresetError('Please enter a valid title for this preset.');
      return;
    }
    setPresetError('');
    if (onSavePreset) {
      onSavePreset(trimmed);
    }
    setPresetSaved(true);
    setTimeout(() => {
      onClose();
    }, 900);
  }, [presetTitle, onSavePreset, onClose]);

  // Keyboard accessibility: Escape to close, Enter to submit
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter' && type === 'preset' && !presetSaved) {
        e.preventDefault();
        handleSavePresetSubmit(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, type, presetSaved, onClose, handleSavePresetSubmit]);

  if (!isOpen) return null;

  // const districtLabel = district === 'all' ? 'India' : district === 'tamilnadu' ? 'Tamil Nadu' : district === 'kerala' ? 'Kerala' : district.charAt(0).toUpperCase() + district.slice(1);
  const districtLabel = (district === 'tamilnadu' || district === 'all') ? 'Tamil Nadu' : district.charAt(0).toUpperCase() + district.slice(1);

  const handleExportSubmit = () => {
    setIsExporting(true);
    setTimeout(() => {
      if (onExport) {
        onExport(exportFormat);
      } else {
        // Fallback synthetic CSV export
        const rows = [
          ['Scope', 'District', 'Sector_Filter', 'Pincode_Filter', 'Export_Timestamp'],
          ['MSME Intelligence Dataset', districtLabel, selectedSector || 'ALL', userPincode || 'ALL', new Date().toISOString()]
        ];
        const csvContent = 'data:text/csv;charset=utf-8,' + rows.map(e => e.join(',')).join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `msme_intelligence_${district}_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setIsExporting(false);
      setExportSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1100);
    }, 600);
  };

  const shareUrl = window.location.href;

  const handleCopyShareLink = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } else {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="intel-modal-backdrop" onClick={onClose}>
      <div 
        className="intel-modal-card"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intel-modal-title"
      >
        <div className="intel-modal-accent-line" />

        {/* Modal Header */}
        <div className="intel-modal-header">
          <div className="intel-modal-title-group">
            <span className="intel-modal-eyebrow">
              {type === 'preset' ? 'SAVE THIS SEARCH' : type === 'export' ? 'DOWNLOAD DATA' : 'SHARE WITH PARTNERS'}
            </span>
            <h3 id="intel-modal-title" className="intel-modal-title">
              {type === 'preset' && 'Save This View'}
              {type === 'export' && 'Download Market Data'}
              {type === 'share' && 'Share This Page With Others'}
            </h3>
          </div>
          <button 
            type="button" 
            className="intel-modal-close-btn" 
            onClick={onClose}
            aria-label="Close dialog"
          >
            <IconClose width={14} height={14} />
          </button>
        </div>

        {/* Modal Scope Summary Tags */}
        <div className="intel-scope-tags-bar">
          <span className="intel-scope-label">Selected Area:</span>
          <div className="intel-scope-pills">
            <span className="intel-scope-pill">
              <IconPin width={11} height={11} /> {districtLabel}
            </span>
            <span className="intel-scope-pill">
              {selectedSector ? `Sector: ${selectedSector}` : 'All Sectors'}
            </span>
            {userPincode && (
              <span className="intel-scope-pill">
                PIN: {userPincode}
              </span>
            )}
          </div>
        </div>

        {/* Body content based on Type */}
        <div className="intel-modal-body">
          {type === 'preset' && (
            <form onSubmit={handleSavePresetSubmit} className="intel-form">
              <div className="intel-input-group">
                <label htmlFor="preset-title-field" className="intel-input-label">
                  Name for this saved view <span className="req-star">*</span>
                </label>
                <div className="intel-input-wrap">
                  <input
                    ref={inputRef}
                    id="preset-title-field"
                    type="text"
                    className={`intel-text-input ${presetError ? 'has-error' : ''}`}
                    value={presetTitle}
                    onChange={(e) => {
                      setPresetTitle(e.target.value);
                      if (presetError) setPresetError('');
                    }}
                    placeholder="e.g. Coimbatore Textile Hub"
                    maxLength={70}
                  />
                  {presetTitle && (
                    <button
                      type="button"
                      className="intel-clear-btn"
                      onClick={() => setPresetTitle('')}
                      aria-label="Clear text"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {presetError && <div className="intel-error-msg">{presetError}</div>}
              </div>

              <div className="intel-suggestions-group">
                <span className="intel-suggestions-label">Suggested Names:</span>
                <div className="intel-chips-row">
                  {suggestions.map((sugg, i) => (
                    <button
                      key={i}
                      type="button"
                      className="intel-chip-btn"
                      onClick={() => {
                        setPresetTitle(sugg);
                        if (presetError) setPresetError('');
                      }}
                    >
                      + {sugg}
                    </button>
                  ))}
                </div>
              </div>

              {presetSaved && (
                <div className="intel-success-banner">
                  <IconCheck width={14} height={14} />
                  <span>Saved to your quick views!</span>
                </div>
              )}

              <div className="intel-modal-footer">
                <button type="button" className="intel-btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="intel-btn-primary"
                  disabled={presetSaved}
                >
                  {presetSaved ? 'Saved ✓' : 'Save View'}
                </button>
              </div>
            </form>
          )}

          {type === 'export' && (
            <div className="intel-export-body">
              <p className="intel-export-desc">
                Download verified market numbers, shop counts, and business trends for this area.
              </p>

              <div className="intel-format-selector">
                <span className="intel-input-label">Choose file format:</span>
                <div className="intel-format-grid">
                  <button
                    type="button"
                    className={`intel-format-card ${exportFormat === 'csv' ? 'active' : ''}`}
                    onClick={() => setExportFormat('csv')}
                  >
                    <strong className="format-title">Excel / CSV File</strong>
                    <span className="format-sub">Simple numbers table</span>
                  </button>
                  <button
                    type="button"
                    className={`intel-format-card ${exportFormat === 'excel' ? 'active' : ''}`}
                    onClick={() => setExportFormat('excel')}
                  >
                    <strong className="format-title">Excel Spreadsheet</strong>
                    <span className="format-sub">Formatted tables ready to view</span>
                  </button>
                  <button
                    type="button"
                    className={`intel-format-card ${exportFormat === 'pdf' ? 'active' : ''}`}
                    onClick={() => setExportFormat('pdf')}
                  >
                    <strong className="format-title">Printable PDF Report</strong>
                    <span className="format-sub">Clean summary page</span>
                  </button>
                </div>
              </div>

              {isExporting && (
                <div className="intel-loading-status">
                  <div className="spinner mini-spinner" />
                  <span>Preparing your download file...</span>
                </div>
              )}

              {exportSuccess && (
                <div className="intel-success-banner">
                  <IconCheck width={14} height={14} />
                  <span>File ready! Download will start now...</span>
                </div>
              )}

              <div className="intel-modal-footer">
                <button type="button" className="intel-btn-secondary" onClick={onClose} disabled={isExporting}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="intel-btn-primary" 
                  onClick={handleExportSubmit}
                  disabled={isExporting || exportSuccess}
                >
                  {isExporting ? 'Preparing...' : exportSuccess ? 'Exported ✓' : 'Download File'}
                </button>
              </div>
            </div>
          )}

          {type === 'share' && (
            <div className="intel-share-body">
              <p className="intel-share-desc">
                Share this exact market view and selected area with your business partners or team.
              </p>

              <div className="intel-share-url-box">
                <input
                  type="text"
                  readOnly
                  className="intel-url-input"
                  value={shareUrl}
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className={`intel-btn-primary ${copied ? 'copied' : ''}`}
                  onClick={handleCopyShareLink}
                >
                  {copied ? (
                    <>
                      <IconCheck width={13} height={13} /> Link Copied!
                    </>
                  ) : (
                    'Copy Link'
                  )}
                </button>
              </div>

              {copied && (
                <div className="intel-success-banner inline-banner">
                  <IconCheck width={14} height={14} />
                  <span>Interactive view URL copied to clipboard!</span>
                </div>
              )}

              <div className="intel-modal-footer">
                <button type="button" className="intel-btn-secondary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
